import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AskKova } from '../components/AskKova';
import { CoachGradeCard } from '../components/CoachGradeCard';
import { FeatureCarousel } from '../components/FeatureCarousel';
import { GameBanner } from '../components/GameBanner';
import { PageBanner } from '../components/PageBanner';
import { GameCard } from '../components/GameCard';
import { SkeletonGrid } from '../components/Skeleton';
import { api, isFinal, isLive, type Game, type League, type LiveAlert } from '../lib/api';
import { byImportance, headlineMixed } from '../lib/importance';
import { useAllGames } from '../lib/useGames';

const abbr = (g: Game) => `${g.away_team.abbreviation} at ${g.home_team.abbreviation}`;


/** Live first, then the biggest games, then finals. See lib/importance. */
const order = byImportance;

/** "All" shows both leagues side by side instead of letting Saturday's college slate fill every slot. */
function mixed(games: Game[], n: number): Game[] {
  const nfl = order(games.filter((g) => (g.league ?? 'nfl') === 'nfl'));
  const cfb = order(games.filter((g) => g.league === 'cfb'));
  const live = [...nfl, ...cfb].filter(isLive);
  const rest = [...nfl.filter((g) => !isLive(g)).slice(0, Math.ceil(n / 2)), ...cfb.filter((g) => !isLive(g)).slice(0, Math.ceil(n / 2))];
  return [...live, ...rest].slice(0, n);
}

function ToolRow({ n, title, text, to }: { n: string; title: string; text: string; to: string }) {
  return (
    <Link to={to} className="group flex items-start gap-4 border-t border-line py-4 transition-colors hover:bg-wash/60">
      <span className="num pt-1 text-[13px] text-blue">{n}</span>
      <div className="min-w-0 flex-1">
        <p className="font-display text-[19px] font-bold leading-tight">{title}</p>
        <p className="body-muted mt-0.5 text-[14px]">{text}</p>
      </div>
      <span className="pt-1 text-blue transition-transform duration-150 group-hover:translate-x-1" aria-hidden="true">&rarr;</span>
    </Link>
  );
}

export function HomePage() {
  const { games, loading } = useAllGames();
  const nav = useNavigate();
  const [league, setLeague] = useState<League | 'all'>('all');
  const [alerts, setAlerts] = useState<(LiveAlert & { game: string })[]>([]);

  // five, not one: on a college Saturday several games have a real claim to the slot
  const featured = useMemo(() => headlineMixed(games, 5), [games]);
  const visible = useMemo(() => {
    const pool = league === 'all' ? mixed(games, 13) : order(games.filter((g) => (g.league ?? 'nfl') === league)).slice(0, 12);
    const shown = new Set(featured.map((g) => g.id));
    return pool.filter((g) => !shown.has(g.id)).slice(0, 12);
  }, [games, league, featured]);
  // The scoreboard rolls to the next slate once a week is over, so by Tuesday it
  // holds nothing finished and these alerts had nothing to grade.
  const [finals, setFinals] = useState<Game[]>([]);
  useEffect(() => {
    let live = true;
    Promise.all((['nfl', 'cfb'] as League[]).map((lg) => api.recentGames(lg).catch(() => [])))
      .then((all) => live && setFinals(all.flat().filter(isFinal)));
    return () => { live = false; };
  }, []);
  // Depend on which games these are, not on the array holding them. An identity
  // that changes every render turned this into a request loop that only stopped
  // when the tab closed, and the fix in useAllGames is one `useMemo` away from
  // being undone by anyone adding a `.filter()` upstream.
  const finalIds = finals.slice(0, 4).map((g) => g.id).join(',');

  useEffect(() => {
    // Coach grades cost nothing (no model call): surface the worst calls from the latest finished games.
    const targets = finals.slice(0, 4);
    if (!targets.length) return;
    Promise.all(targets.map((g) => api.liveGrade(g.id, g.league ?? 'nfl').then((r) => (r.live_alerts ?? []).map((a) => ({ ...a, game: abbr(g) }))).catch(() => [])))
      .then((all) => {
        const sorted = all.flat().sort((x, y) => y.wp_cost_pct - x.wp_cost_pct);
        const seen = new Set<string>();
        const distinct = sorted.filter((a) => (seen.has(a.category) ? false : (seen.add(a.category), true)));
        setAlerts([...distinct, ...sorted.filter((a) => !distinct.includes(a))].slice(0, 3));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalIds]);

  return (
    <>
      {/* The placeholder has to be the same height as the banner that replaces it.
          A short block growing into a 340px one shifted everything below it, which
          is where a Cumulative Layout Shift of 0.161 was coming from. */}
      {featured.length ? <FeatureCarousel games={featured} render={(g) => <GameBanner g={g} />} /> : (
        <PageBanner fill="none" style={{ background: '#14161a' }}>
          <h1 className="display text-[34px] leading-[0.95] md:text-[44px]">The game behind the game.</h1>
          <p className="mt-2.5 max-w-[48ch] text-[15px] text-paper/70">Loading this week.</p>
        </PageBanner>
      )}

      {/* the ask bar sits directly under the scoreboard, the way a search bar does on a sports site */}
      <section className="border-b border-line bg-wash">
        <div className="container flex flex-col gap-3 py-4 md:flex-row md:items-center">
          <span className="kicker shrink-0">Ask Kova</span>
          <div className="min-w-0 flex-1"><AskKova onAsk={(q) => nav(`/ask?q=${encodeURIComponent(q)}`)} examples={false} /></div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="kicker">Scoreboard</p>
              <h2 className="h2 mt-1">{visible.some(isLive) ? 'Live now.' : 'This week.'}</h2>
            </div>
            <div className="seg">
              {([['all', 'All'], ['nfl', 'NFL'], ['cfb', 'CFB']] as const).map(([id, label]) => (
                <button key={id} onClick={() => setLeague(id)} className={`seg-item ${league === id ? 'seg-item-on' : ''}`}>{label}</button>
              ))}
            </div>
          </div>
          {loading && !visible.length ? <SkeletonGrid n={8} /> : visible.length ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {visible.map((g) => <GameCard key={g.id} g={g} />)}
            </div>
          ) : <p className="body-muted">No games on the schedule right now.</p>}
          <div className="mt-5 flex gap-2">
            <Link to="/nfl" className="chip">All NFL</Link>
            <Link to="/college" className="chip">All college</Link>
          </div>
        </div>
      </section>

      {alerts.length > 0 && (
        <section className="border-y border-line bg-wash py-10">
          <div className="container">
            <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="kicker">Coach grades</p>
                <h2 className="h2 mt-1">The calls that cost them.</h2>
              </div>
              <Link to="/grades" className="chip">Leaderboard</Link>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {alerts.map((a, i) => <CoachGradeCard key={i} d={a} team={a.team} game={a.game} />)}
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="container grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
          <div>
            <p className="kicker">What Kova does</p>
            <h2 className="h2 mt-1">Four questions,<br />answered with the math.</h2>
            <p className="body-muted mt-3 max-w-[42ch]">Every number comes from ten seasons of play-by-play and the live market. The model explains; it never does the arithmetic.</p>
          </div>
          <div className="border-b border-line">
            <ToolRow n="01" title="Predict" text="A winner, an expected margin, and the three factors that decided it." to="/predict" />
            <ToolRow n="02" title="Betting coach" text="Kova's probability against the market, with edge, expected value and a stake." to="/betting" />
            <ToolRow n="03" title="Coach grades" text="Every fourth down, kick and timeout graded against what usually works." to="/grades" />
            <ToolRow n="04" title="Breakdown" text="What actually decided a finished game, and what only looked like it did." to="/breakdown" />
          </div>
        </div>
      </section>
    </>
  );
}
